import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Input,
  Tag,
  Typography,
  Card,
  Form,
  Select,
  InputNumber,
  Switch,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { SubscriptionService } from '../../services/api/subscriptionService';
import { SubscriptionType } from '../../types/subscription';
import { SplitPaneLayout } from '../common/SplitPaneLayout';
import { ManagementForm } from '../common/ManagementForm';
import { FeatureChecklist } from './FeatureChecklist';
import { JsonSchemaEditor } from '../Krybot/AgentManagement/JsonSchemaEditor';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { PrimeArtifact } from '../../types/primeArtifact';
import '../../styles/management-pages.css';

const { Search } = Input;
const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface SubscriptionTypeListProps {
  onSubscriptionTypeChange?: () => void;
}

export const SubscriptionTypeList: React.FC<SubscriptionTypeListProps> = ({ onSubscriptionTypeChange }) => {
  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionType[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();
  const [primeArtifacts, setPrimeArtifacts] = useState<PrimeArtifact[]>([]);
  const [loadingPrimeArtifacts, setLoadingPrimeArtifacts] = useState(false);

  useEffect(() => {
    loadSubscriptionTypes();
    loadPrimeArtifacts();
  }, []);

  const loadSubscriptionTypes = async () => {
    setLoading(true);
    try {
      const typeList = await SubscriptionService.getSubscriptionTypes();
      setSubscriptionTypes(typeList);
    } catch (err: any) {
      message.error(err.message || 'Failed to load subscription types');
    } finally {
      setLoading(false);
    }
  };

  const loadPrimeArtifacts = async () => {
    setLoadingPrimeArtifacts(true);
    try {
      const artifacts = await PrimeArtifactService.getAll();
      setPrimeArtifacts(artifacts);
    } catch (error) {
      console.error('[SubscriptionTypeList] Error fetching prime artifacts:', error);
      setPrimeArtifacts([]);
    } finally {
      setLoadingPrimeArtifacts(false);
    }
  };

  const selectedType = selectedTypeId ? subscriptionTypes.find(t => t.id === selectedTypeId) : null;

  const filteredSubscriptionTypes = subscriptionTypes.filter((type) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      type.code.toLowerCase().includes(searchLower) ||
      type.name.toLowerCase().includes(searchLower) ||
      (type.description || '').toLowerCase().includes(searchLower)
    );
  });

  const handleSelectType = async (type: SubscriptionType) => {
    setSelectedTypeId(type.id);
    setIsCreating(false);
    
    // Fetch the latest subscription type data to ensure we have features
    try {
      const latestType = await SubscriptionService.getSubscriptionTypeById(type.id);
      if (latestType) {
        // Set form values
        form.setFieldsValue({
          code: latestType.code,
          name: latestType.name,
          description: latestType.description || '',
          billing_period: latestType.billing_period,
          currency: latestType.currency || 'USD',
          base_price: latestType.base_price,
          is_active: latestType.is_active,
          feature_ids: latestType.features?.map(f => f.id) || [],
          prime_artifact_id: latestType.prime_artifact_id,
          metadata: latestType.metadata || {},
        });
      }
    } catch (err) {
      console.error('[SubscriptionTypeList] Error fetching subscription type for edit:', err);
      // Fallback to type from table
      form.setFieldsValue({
        code: type.code,
        name: type.name,
        description: type.description || '',
        billing_period: type.billing_period,
        currency: type.currency || 'USD',
        base_price: type.base_price,
        is_active: type.is_active,
        feature_ids: type.features?.map(f => f.id) || [],
        prime_artifact_id: type.prime_artifact_id,
        metadata: type.metadata || {},
      });
    }
  };

  const handleCreateType = () => {
    const tempId = `temp-${Date.now()}`;
    const newType: SubscriptionType = {
      id: tempId,
      code: '',
      name: '',
      billing_period: 'monthly',
      currency: 'USD',
      is_active: true,
      features: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setSubscriptionTypes([...subscriptionTypes, newType]);
    setSelectedTypeId(tempId);
    setIsCreating(true);
    form.resetFields();
    form.setFieldsValue({
      currency: 'USD',
      is_active: true,
      billing_period: 'monthly',
      feature_ids: [],
      metadata: {},
    });
  };

  const handleSave = async (values: any) => {
    setIsSaving(true);
    try {
      if (isCreating && selectedTypeId?.startsWith('temp-')) {
        // Create new type
        const createData = {
          code: values.code.trim().toUpperCase(),
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
          billing_period: values.billing_period,
          base_price: values.base_price !== undefined && values.base_price !== null ? values.base_price : undefined,
          currency: values.currency || 'USD',
          is_active: values.is_active !== undefined ? values.is_active : true,
          feature_ids: values.feature_ids || [],
          prime_artifact_id: values.prime_artifact_id || undefined,
          metadata: values.metadata || undefined,
        };

        // Transform to backend format
        const backendData = {
          subscription_code: createData.code,
          subscription_name: createData.name,
          description: createData.description,
          billing_period: createData.billing_period,
          base_price: createData.base_price,
          currency: createData.currency || 'USD',
          is_active: createData.is_active !== undefined ? createData.is_active : true,
          feature_ids: createData.feature_ids || [],
          prime_artifact_id: createData.prime_artifact_id,
          metadata: createData.metadata,
        };

        const newType = await SubscriptionService.createSubscriptionType(backendData as any);
        message.success('Subscription type created successfully');
        
        // Remove temp item and add real one
        setSubscriptionTypes(subscriptionTypes.filter(t => t.id !== selectedTypeId).concat(newType));
        setSelectedTypeId(newType.id);
        setIsCreating(false);
        onSubscriptionTypeChange?.();
      } else if (selectedType) {
        // Update existing type
        const updateData = {
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
          billing_period: values.billing_period,
          base_price: values.base_price !== undefined && values.base_price !== null ? values.base_price : undefined,
          currency: values.currency || 'USD',
          is_active: values.is_active !== undefined ? values.is_active : true,
          feature_ids: values.feature_ids || [],
          prime_artifact_id: values.prime_artifact_id || undefined,
          metadata: values.metadata || undefined,
        };

        // Transform to backend format
        const backendData = {
          subscription_name: updateData.name,
          description: updateData.description,
          billing_period: updateData.billing_period,
          base_price: updateData.base_price,
          currency: updateData.currency || 'USD',
          is_active: updateData.is_active !== undefined ? updateData.is_active : true,
          feature_ids: updateData.feature_ids || [],
          prime_artifact_id: updateData.prime_artifact_id,
          metadata: updateData.metadata,
        };

        const updatedType = await SubscriptionService.updateSubscriptionType(selectedType.id, backendData as any);
        message.success('Subscription type updated successfully');
        
        // Update in list
        setSubscriptionTypes(subscriptionTypes.map(t => t.id === selectedType.id ? updatedType : t));
        setSelectedTypeId(updatedType.id);
        onSubscriptionTypeChange?.();
      }
      
      await loadSubscriptionTypes();
    } catch (error: any) {
      console.error('Error saving subscription type:', error);
      
      // Handle validation errors (array format)
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const errorMessages = error.response.data.detail
          .map((err: any) => {
            if (typeof err === 'string') return err;
            return `${err.loc?.join('.')}: ${err.msg || err.message || 'Validation error'}`;
          })
          .join(', ');
        message.error(`Validation error: ${errorMessages}`);
      } else {
        // Handle single error message
        const detail = error.response?.data?.detail;
        const errorMsg = typeof detail === 'string' ? detail : error.message || 'Failed to save subscription type';
        message.error(errorMsg);
      }
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreating && selectedTypeId?.startsWith('temp-')) {
      // Remove temp item from list
      setSubscriptionTypes(subscriptionTypes.filter(t => t.id !== selectedTypeId));
    }
    setSelectedTypeId(null);
    setIsCreating(false);
    form.resetFields();
  };

  const handleDelete = async () => {
    if (!selectedType) return;
    
    try {
      await SubscriptionService.deleteSubscriptionType(selectedType.id);
      message.success('Subscription type deleted successfully');
      setSelectedTypeId(null);
      setIsCreating(false);
      await loadSubscriptionTypes();
      onSubscriptionTypeChange?.();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete subscription type');
      throw error;
    }
  };

  const handleDeleteFromTable = async (id: string) => {
    try {
      await SubscriptionService.deleteSubscriptionType(id);
      message.success('Subscription type deleted successfully');
      if (selectedTypeId === id) {
        setSelectedTypeId(null);
        setIsCreating(false);
      }
      await loadSubscriptionTypes();
      onSubscriptionTypeChange?.();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete subscription type');
    }
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (text: string, type: SubscriptionType) => (
        <Button
          type="link"
          onClick={() => handleSelectType(type)}
          style={{ padding: 0 }}
        >
          <Text strong>{text}</Text>
        </Button>
      ),
      sorter: (a: SubscriptionType, b: SubscriptionType) => a.code.localeCompare(b.code),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, type: SubscriptionType) => (
        <Button
          type="link"
          onClick={() => handleSelectType(type)}
          style={{ padding: 0 }}
        >
          <Text>{text}</Text>
        </Button>
      ),
      sorter: (a: SubscriptionType, b: SubscriptionType) => a.name.localeCompare(b.name),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Features',
      key: 'features',
      render: (_: any, record: SubscriptionType) => (
        <Tag color="blue">{record.features?.length || 0}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, type: SubscriptionType) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleSelectType(type)}
            title="Edit subscription type"
          />
          <Popconfirm
            title="Are you sure you want to delete this subscription type?"
            description="This action cannot be undone. Make sure this subscription type is not used by any subscriptions."
            onConfirm={() => handleDeleteFromTable(type.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete subscription type"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const formTabs = [
    {
      key: 'basic',
      label: 'Basic Info',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Form.Item
            name="code"
            label="Subscription Type Code"
            rules={[
              { required: true, message: 'Please enter subscription type code' },
              { pattern: /^[A-Z_][A-Z0-9_]*$/, message: 'Code must be uppercase with underscores (e.g., KRYSALIS_CORE)' },
            ]}
          >
            <Input 
              placeholder="e.g., KRYSALIS_CORE" 
              disabled={!isCreating}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Subscription Type Name"
            rules={[{ required: true, message: 'Please enter subscription type name' }]}
          >
            <Input placeholder="e.g., Krysalis Core" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Enter subscription type description"
            />
          </Form.Item>

          <Form.Item
            name="billing_period"
            label="Billing Period"
            rules={[{ required: true, message: 'Please select billing period' }]}
          >
            <Select placeholder="Select billing period">
              <Option value="monthly">Monthly</Option>
              <Option value="yearly">Yearly</Option>
              <Option value="one-time">One-Time</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="currency"
            label="Currency"
          >
            <Select placeholder="Select currency">
              <Option value="USD">USD ($)</Option>
              <Option value="EUR">EUR (€)</Option>
              <Option value="GBP">GBP (£)</Option>
              <Option value="CAD">CAD (C$)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="base_price"
            label="Base Price"
            tooltip="Optional base price for this subscription type"
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Space>
      ),
    },
    {
      key: 'features',
      label: 'Features',
      children: (
        <Form.Item
          name="feature_ids"
          label="Features"
          rules={[
            { required: true, message: 'Please select at least one feature' },
            { type: 'array', min: 1, message: 'Please select at least one feature' },
          ]}
        >
          <FeatureChecklist />
        </Form.Item>
      ),
    },
    {
      key: 'advanced',
      label: 'Advanced',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Form.Item
            name="prime_artifact_id"
            label="Prime Artifact"
            tooltip="Optional: Link this subscription type to a prime artifact"
          >
            <Select
              placeholder="Select a prime artifact (optional)"
              allowClear
              loading={loadingPrimeArtifacts}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const label = option?.label;
                if (typeof label === 'string') {
                  return label.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
            >
              {primeArtifacts.map((artifact) => (
                <Option key={artifact.id} value={artifact.id} label={artifact.name}>
                  {artifact.name} {artifact.description ? `- ${artifact.description}` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="metadata"
            label="Metadata (JSON)"
            tooltip="Additional metadata for the subscription type"
          >
            <JsonSchemaEditor
              key={`metadata-${selectedTypeId || 'new'}`}
              fieldType="input_schema"
              placeholder='{"key": "value"}'
              description="Subscription type metadata in JSON format. Use code mode to edit directly."
            />
          </Form.Item>
        </Space>
      ),
    },
  ];

  return (
    <div className="management-page-container">
      <div className="management-page-header">
        <Title level={2}>Subscription Types</Title>
        <Text type="secondary">Manage subscription type definitions</Text>
      </div>

      <SplitPaneLayout
        leftContent={
          <Card>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Search
                  placeholder="Search subscription types..."
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="large"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateType}
                >
                  New
                </Button>
              </Space>

              <Table
                columns={columns}
                dataSource={filteredSubscriptionTypes}
                rowKey="id"
                loading={loading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} subscription types`,
                }}
                rowClassName={(record) => 
                  record.id === selectedTypeId ? 'ant-table-row-selected' : ''
                }
                onRow={(record) => ({
                  onClick: () => handleSelectType(record),
                  style: { cursor: 'pointer' },
                })}
              />
            </Space>
          </Card>
        }
        rightContent={
          selectedTypeId ? (
            <ManagementForm
              title={isCreating ? 'Create Subscription Type' : `Edit: ${selectedType?.code || ''}`}
              isCreating={isCreating}
              isLoading={false}
              isSaving={isSaving}
              form={form}
              tabs={formTabs}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={handleDelete}
              showDelete={!isCreating}
            />
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }}>
                <Text>Select a subscription type from the list to view or edit</Text>
              </div>
            </Card>
          )
        }
      />
    </div>
  );
};
